/**
 * Map Legend Component
 * Shows color coding legend for tracts (by TractType) and wells (by ENVWellStatus)
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { symbologyConfig } from '../config/gisConfig';

function MapLegend({ visibleLayers = {} }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('tracts');

  // Determine which layers are currently shown
  const showTracts = visibleLayers.tracts;
  const showWells = visibleLayers.wells;

  // Auto-select tab based on visible layers (must be before any early return)
  React.useEffect(() => {
    if (showWells && !showTracts) {
      setActiveTab('wells');
    } else if (showTracts && !showWells) {
      setActiveTab('tracts');
    }
  }, [showTracts, showWells]);

  // If no relevant layers visible, don't show legend
  if (!showTracts && !showWells) {
    return null;
  }

  const tractLegendItems = [
    { type: 'F', ...symbologyConfig.tractTypes['F'] },
    { type: 'O', ...symbologyConfig.tractTypes['O'] },
    { type: 'N', ...symbologyConfig.tractTypes['N'] },
    { type: 'U', ...symbologyConfig.tractTypes['U'] },
    { type: 'Q', ...symbologyConfig.tractTypes['Q'] },
    { type: 'V', ...symbologyConfig.tractTypes['V'] }
  ];

  const wellLegendItems = [
    { status: 'PRODUCING', ...symbologyConfig.wellStatuses['PRODUCING'] },
    { status: 'DUC', ...symbologyConfig.wellStatuses['DUC'] },
    { status: 'DRILLING', ...symbologyConfig.wellStatuses['DRILLING'] },
    { status: 'PERMITTED', ...symbologyConfig.wellStatuses['PERMITTED'] },
    { status: 'COMPLETED', ...symbologyConfig.wellStatuses['COMPLETED'] },
    { status: 'DRILLED', ...symbologyConfig.wellStatuses['DRILLED'] },
    { status: 'INACTIVE', label: 'Inactive/Shut-in', color: '#6b7280' },
    { status: 'P & A', ...symbologyConfig.wellStatuses['P & A'] }
  ];

  return (
    <div className="map-legend">
      <div
        className="legend-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Layers size={16} />
        <span>Legend</span>
        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>

      {isExpanded && (
        <div className="legend-content">
          {/* Tab buttons - only show if both layers visible */}
          {showTracts && showWells && (
            <div className="legend-tabs">
              <button
                className={`legend-tab ${activeTab === 'tracts' ? 'active' : ''}`}
                onClick={() => setActiveTab('tracts')}
              >
                Tracts
              </button>
              <button
                className={`legend-tab ${activeTab === 'wells' ? 'active' : ''}`}
                onClick={() => setActiveTab('wells')}
              >
                Wells
              </button>
            </div>
          )}

          {/* Tract Legend */}
          {(activeTab === 'tracts' && showTracts) && (
            <div className="legend-section">
              <div className="legend-title">Tract Type</div>
              <div className="legend-items">
                {tractLegendItems.map(item => (
                  <div key={item.type} className="legend-item">
                    <div
                      className="legend-color-box"
                      style={{
                        backgroundColor: item.fillColor,
                        borderColor: item.color
                      }}
                    />
                    <span className="legend-label">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Well Legend */}
          {(activeTab === 'wells' && showWells) && (
            <div className="legend-section">
              <div className="legend-title">Well Status</div>
              <div className="legend-items">
                {wellLegendItems.map(item => (
                  <div key={item.status} className="legend-item">
                    <div
                      className="legend-color-line"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="legend-label">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MapLegend;
